import {
  CheckCircleIcon,
  ChatBubbleLeftRightIcon,
  ClockIcon,
  DocumentTextIcon,
  ExclamationCircleIcon,
  EyeIcon,
  PencilSquareIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { ErrorMessage } from '../../components/ErrorMessage/ErrorMessage';
import { LoadingSpinner } from '../../components/LoadingSpinner/LoadingSpinner';
import { projectService } from '../../services/projectService';
import { reviewService } from '../../services/reviewService';
import { ProjectItem, ProjectStatus } from '../../types/project';
import {
  REVIEW_PAGE_SIZE_MAX,
  ReviewSummary,
  ReviewerAssignment,
  ReviewStatus,
  ReviewerStatus,
} from '../../types/review';

const ITEMS_PER_PAGE = 20;
const REVIEW_FETCH_LIMIT = REVIEW_PAGE_SIZE_MAX;
const INCOMPLETE_REVIEW_CARD_ID_PREFIX = 'project-';
const APPROVER_MISSING_LABEL = 'Approver Missing';
const REVIEW_SETUP_INCOMPLETE_LABEL = 'Review Setup Incomplete';
const APPROVER_ROLE = 'approver';

// Tab identifiers for the two-tab review view.
const TAB_MY_ARCHITECTURES = 'my-architectures';
const TAB_ASSIGNED_TO_ME = 'assigned-to-me';

type ReviewTab = typeof TAB_MY_ARCHITECTURES | typeof TAB_ASSIGNED_TO_ME;

type MyArchitectureReviewItem = ReviewSummary & {
  review_id?: string | null;
  requires_review_setup?: boolean;
};

const createIncompleteReviewItem = (
  project: ProjectItem,
): MyArchitectureReviewItem => ({
  id: `${INCOMPLETE_REVIEW_CARD_ID_PREFIX}${project.id}`,
  review_id: project.review_summary.review_id,
  project_id: project.id,
  project_name: project.title,
  status: 'in_review',
  reviewer_count: 0,
  approver_count: 0,
  approver_missing: true,
  approved_count: 0,
  pending_count: 0,
  changes_requested_count: 0,
  comment_count: 0,
  updated_at: project.updated_at,
  requires_review_setup: true,
});

/**
 * ArchitectureReviewScreen - Architecture review workflow management
 * Two-tab view: "My Architectures" and "Assigned to Me"
 */
export const ArchitectureReviewScreen: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<ReviewTab>(
    TAB_MY_ARCHITECTURES,
  );

  // My Architectures state
  const [myReviews, setMyReviews] = useState<ReviewSummary[]>([]);
  const [myReviewsLoading, setMyReviewsLoading] = useState(false);
  const [myReviewsError, setMyReviewsError] = useState<string | null>(null);
  const [myReviewsTotal, setMyReviewsTotal] = useState(0);
  const [myReviewsOffset, setMyReviewsOffset] = useState(0);

  // Assigned to Me state
  const [assignedReviews, setAssignedReviews] = useState<ReviewerAssignment[]>([]);
  const [assignedLoading, setAssignedLoading] = useState(false);
  const [assignedError, setAssignedError] = useState<string | null>(null);
  const [assignedTotal, setAssignedTotal] = useState(0);
  const [assignedOffset, setAssignedOffset] = useState(0);

  /**
   * Load my architectures under review
   */
  const loadMyReviews = async (offset = 0) => {
    setMyReviewsLoading(true);
    setMyReviewsError(null);
    try {
      const [reviewsResponse, ownedProjects] = await Promise.all([
        reviewService.getMyReviews(undefined, REVIEW_FETCH_LIMIT, 0),
        projectService.getOwnedProjects(),
      ]);

      const reviewItems = (reviewsResponse.reviews || []).map((review) => ({
        ...review,
        review_id: review.id,
      }));
      const reviewProjectIds = new Set(reviewItems.map((review) => review.project_id));
      const incompleteReviewItems = ownedProjects
        .filter(
          (project) =>
            project.status === ProjectStatus.IN_REVIEW &&
            !reviewProjectIds.has(project.id) &&
            !project.review_summary.review_id,
        )
        .map(createIncompleteReviewItem);

      const mergedReviews = [...reviewItems, ...incompleteReviewItems].sort(
        (left, right) =>
          new Date(right.updated_at).getTime() -
          new Date(left.updated_at).getTime(),
      );

      setMyReviews(mergedReviews.slice(offset, offset + ITEMS_PER_PAGE));
      setMyReviewsTotal(mergedReviews.length);
      setMyReviewsOffset(offset);
    } catch (err) {
      setMyReviewsError(
        err instanceof Error ? err.message : 'Failed to load reviews',
      );
    } finally {
      setMyReviewsLoading(false);
    }
  };

  /**
   * Load reviews assigned to me
   */
  const loadAssignedReviews = async (offset = 0) => {
    setAssignedLoading(true);
    setAssignedError(null);
    try {
      const response = await reviewService.getAssignedReviews(
        undefined,
        ITEMS_PER_PAGE,
        offset,
      );
      setAssignedReviews(response.reviews || []);
      setAssignedTotal(response.total);
      setAssignedOffset(offset);
    } catch (err) {
      setAssignedError(
        err instanceof Error ? err.message : 'Failed to load assigned reviews',
      );
    } finally {
      setAssignedLoading(false);
    }
  };

  /**
   * Load data when tab changes
   */
  useEffect(() => {
    if (activeTab === TAB_MY_ARCHITECTURES) {
      loadMyReviews(0);
    } else {
      loadAssignedReviews(0);
    }
  }, [activeTab]);

  /**
   * Get status badge styling
   */
  const getStatusBadge = (
    status: ReviewStatus | ReviewerStatus,
  ): JSX.Element => {
    const styles: Record<string, string> = {
      in_review: 'bg-blue-100 text-blue-800',
      approved: 'bg-green-100 text-green-800',
      changes_requested: 'bg-yellow-100 text-yellow-800',
      closed: 'bg-gray-100 text-gray-800',
      pending: 'bg-gray-100 text-gray-600',
    };

    const labels: Record<string, string> = {
      in_review: 'In Review',
      approved: 'Approved',
      changes_requested: 'Changes Requested',
      closed: 'Closed',
      pending: 'Pending',
    };

    const icons: Record<string, JSX.Element> = {
      in_review: <ClockIcon className="w-3 h-3" />,
      approved: <CheckCircleIcon className="w-3 h-3" />,
      changes_requested: <ExclamationCircleIcon className="w-3 h-3" />,
      closed: <CheckCircleIcon className="w-3 h-3" />,
      pending: <ClockIcon className="w-3 h-3" />,
    };

    return (
      <span
        className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status] || styles['pending']}`}
      >
        {icons[status] || icons['pending']}
        {labels[status] || status}
      </span>
    );
  };

  /**
   * Navigate to review detail view
   */
  const handleViewReview = (review: MyArchitectureReviewItem) => {
    if (review.review_id) {
      navigate(`/reviews/${review.review_id}?projectId=${review.project_id}`);
      return;
    }

    navigate(`/project/${review.project_id}/review`);
  };

  /**
   * Render My Architectures tab
   */
  const renderMyArchitectures = (): JSX.Element => {
    if (myReviewsLoading) {
      return (
        <div className="flex justify-center items-center py-12">
          <LoadingSpinner />
        </div>
      );
    }

    if (myReviewsError) {
      return (
        <ErrorMessage
          message={myReviewsError}
          onDismiss={() => setMyReviewsError(null)}
        />
      );
    }

    if (myReviews.length === 0) {
      return (
        <div className="text-center py-16">
          <div className="mb-6">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-full">
              <DocumentTextIcon className="w-10 h-10 text-gray-400" />
            </div>
          </div>
          <h3 className="text-2xl font-semibold text-gray-500 mb-3">
            No Reviews Yet
          </h3>
          <p className="text-gray-400 max-w-md mx-auto">
            You haven&apos;t submitted any architectures for review yet.
            Submit your first architecture to start the review process.
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {myReviews.map((review) => (
          <div
            key={review.id}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {review.project_name}
                </h3>
                <div className="flex items-center gap-3 text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <UserGroupIcon className="w-4 h-4" />
                    {review.reviewer_count} reviewer
                    {review.reviewer_count !== 1 ? 's' : ''}
                  </span>
                  {review.approver_missing && (
                    <>
                      <span>•</span>
                      <span className="text-amber-700 font-medium">
                        {APPROVER_MISSING_LABEL}
                      </span>
                    </>
                  )}
                  <span>•</span>
                  <span>
                    Updated {new Date(review.updated_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
              <div>{getStatusBadge(review.status)}</div>
            </div>

            <div className="flex items-center gap-4 mb-4">
              <div className="flex items-center gap-2 text-sm">
                <CheckCircleIcon className="w-5 h-5 text-green-600" />
                <span className="font-medium">{review.approved_count}</span>
                <span className="text-gray-500">Approved</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <UserGroupIcon className="w-5 h-5 text-blue-600" />
                <span className="font-medium">{review.approver_count}</span>
                <span className="text-gray-500">Approvers</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <ClockIcon className="w-5 h-5 text-gray-400" />
                <span className="font-medium">{review.pending_count}</span>
                <span className="text-gray-500">Pending</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <ExclamationCircleIcon className="w-5 h-5 text-yellow-600" />
                <span className="font-medium">
                  {review.changes_requested_count}
                </span>
                <span className="text-gray-500">Changes Requested</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <ChatBubbleLeftRightIcon className="w-5 h-5 text-gray-500" />
                <span className="font-medium">{review.comment_count}</span>
                <span className="text-gray-500">Comments</span>
              </div>
            </div>

            {review.requires_review_setup && (
              <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                {REVIEW_SETUP_INCOMPLETE_LABEL}. This project is already in review stage,
                but no review record exists yet. Open Final Review to complete the
                submission.
              </div>
            )}

            {review.approver_missing && !review.requires_review_setup && (
              <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                {APPROVER_MISSING_LABEL}. You can add an approver later from the
                review detail page.
              </div>
            )}

            <div className="flex items-center gap-3">
              <button
                onClick={() => handleViewReview(review)}
                className="flex items-center gap-2 px-4 py-2 bg-[#336094] text-white rounded-md hover:bg-[#2a4f7a] transition-colors"
              >
                <EyeIcon className="w-4 h-4" />
                {review.requires_review_setup ? 'Open Final Review' : 'View Review'}
              </button>
            </div>
          </div>
        ))}

        {/* Pagination */}
        {myReviewsTotal > ITEMS_PER_PAGE && (
          <div className="flex justify-center items-center gap-4 pt-6">
            <button
              onClick={() => loadMyReviews(myReviewsOffset - ITEMS_PER_PAGE)}
              disabled={myReviewsOffset === 0}
              className="px-4 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Previous
            </button>
            <span className="text-sm text-gray-600">
              {myReviewsOffset + 1} -{' '}
              {Math.min(myReviewsOffset + ITEMS_PER_PAGE, myReviewsTotal)} of{' '}
              {myReviewsTotal}
            </span>
            <button
              onClick={() => loadMyReviews(myReviewsOffset + ITEMS_PER_PAGE)}
              disabled={myReviewsOffset + ITEMS_PER_PAGE >= myReviewsTotal}
              className="px-4 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        )}
      </div>
    );
  };

  /**
   * Render Assigned to Me tab
   */
  const renderAssignedToMe = (): JSX.Element => {
    if (assignedLoading) {
      return (
        <div className="flex justify-center items-center py-12">
          <LoadingSpinner />
        </div>
      );
    }

    if (assignedError) {
      return (
        <ErrorMessage
          message={assignedError}
          onDismiss={() => setAssignedError(null)}
        />
      );
    }

    if (assignedReviews.length === 0) {
      return (
        <div className="text-center py-16">
          <div className="mb-6">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-full">
              <UserGroupIcon className="w-10 h-10 text-gray-400" />
            </div>
          </div>
          <h3 className="text-2xl font-semibold text-gray-500 mb-3">
            No Pending Reviews
          </h3>
          <p className="text-gray-400 max-w-md mx-auto">
            You don&apos;t have any architectures assigned for review at the
            moment.
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {assignedReviews.map((assignment) => (
          <div
            key={assignment.id}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {assignment.project_name}
                </h3>
                <div className="flex items-center gap-3 text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <UserGroupIcon className="w-4 h-4" />
                    {assignment.reviewer_count} reviewer
                    {assignment.reviewer_count !== 1 ? 's' : ''}
                  </span>
                  <span>•</span>
                  <span>
                    Updated{' '}
                    {new Date(assignment.updated_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Project Status:</span>
                  {getStatusBadge(assignment.review_status)}
                </div>
                {assignment.my_role === APPROVER_ROLE && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">
                      Your Decision:
                    </span>
                    {getStatusBadge(assignment.my_status)}
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-4 mb-4">
              <div className="flex items-center gap-2 text-sm">
                <CheckCircleIcon className="w-5 h-5 text-green-600" />
                <span className="font-medium">{assignment.approved_count}</span>
                <span className="text-gray-500">Approved</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <ClockIcon className="w-5 h-5 text-gray-400" />
                <span className="font-medium">{assignment.pending_count}</span>
                <span className="text-gray-500">Pending</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <ExclamationCircleIcon className="w-5 h-5 text-yellow-600" />
                <span className="font-medium">
                  {assignment.changes_requested_count}
                </span>
                <span className="text-gray-500">Changes Requested</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <ChatBubbleLeftRightIcon className="w-5 h-5 text-gray-500" />
                <span className="font-medium">{assignment.comment_count}</span>
                <span className="text-gray-500">Comments</span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() =>
                  navigate(
                    `/reviews/${assignment.id}?projectId=${assignment.project_id}`,
                  )
                }
                className="flex items-center gap-2 px-4 py-2 bg-[#336094] text-white rounded-md hover:bg-[#2a4f7a] transition-colors"
              >
                <PencilSquareIcon className="w-4 h-4" />
                Review Architecture
              </button>
            </div>
          </div>
        ))}

        {/* Pagination */}
        {assignedTotal > ITEMS_PER_PAGE && (
          <div className="flex justify-center items-center gap-4 pt-6">
            <button
              onClick={() =>
                loadAssignedReviews(assignedOffset - ITEMS_PER_PAGE)
              }
              disabled={assignedOffset === 0}
              className="px-4 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Previous
            </button>
            <span className="text-sm text-gray-600">
              {assignedOffset + 1} -{' '}
              {Math.min(assignedOffset + ITEMS_PER_PAGE, assignedTotal)} of{' '}
              {assignedTotal}
            </span>
            <button
              onClick={() =>
                loadAssignedReviews(assignedOffset + ITEMS_PER_PAGE)
              }
              disabled={assignedOffset + ITEMS_PER_PAGE >= assignedTotal}
              className="px-4 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="w-full">
      <div className="w-full">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Architecture Reviews
          </h1>
          <p className="text-gray-600">
            Manage architecture reviews and collaborate with your team
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex space-x-8" aria-label="Tabs">
            <button
              onClick={() => setActiveTab(TAB_MY_ARCHITECTURES)}
              className={`${
                activeTab === TAB_MY_ARCHITECTURES
                  ? 'border-[#008557] text-[#008557]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2`}
            >
              <DocumentTextIcon className="w-4 h-4" />
              My Architectures
              {myReviewsTotal > 0 && (
                <span className="ml-2 bg-gray-100 text-gray-900 py-0.5 px-2 rounded-full text-xs font-medium">
                  {myReviewsTotal}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab(TAB_ASSIGNED_TO_ME)}
              className={`${
                activeTab === TAB_ASSIGNED_TO_ME
                  ? 'border-[#008557] text-[#008557]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2`}
            >
              <UserGroupIcon className="w-4 h-4" />
              Assigned to Me
              {assignedTotal > 0 && (
                <span className="ml-2 bg-gray-100 text-gray-900 py-0.5 px-2 rounded-full text-xs font-medium">
                  {assignedTotal}
                </span>
              )}
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <div>
          {activeTab === TAB_MY_ARCHITECTURES
            ? renderMyArchitectures()
            : renderAssignedToMe()}
        </div>
      </div>
    </div>
  );
};
